import { createContext, useState, useEffect, useContext } from 'react';
import { getCategories } from '../services/appStoreService';
import { getUserLcid, getMultiLingualString } from '../utilities/multiLingual';

const CategoryContext = createContext({
    categories: [],
    isLoading: false,
    error: null,
    getCategoryById: () => null,
    getCategoryTitle: () => '',
    getCategoryDescription: () => ''
});

export const CategoryProvider = ({ children }) => {
    const [categories, setCategories] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const userLcid = getUserLcid();

    useEffect(() => {
        const loadCategories = async () => {
            try {
                setIsLoading(true);
                setError(null);
                const categoryList = await getCategories(userLcid);
                setCategories(categoryList);
            } catch (err) {
                console.error('Failed to load categories:', err);
                setError(err.message || 'Failed to load categories');
            } finally {
                setIsLoading(false);
            }
        };

        loadCategories();
    }, [userLcid]);

    /**
     * Get category object by ID
     * @param {string} categoryId - The category ID (e.g., "analytics")
     * @returns {object|null} The category object or null if not found
     */
    const getCategoryById = (categoryId) => {
        if (!categoryId) return null;
        return categories.find(cat => cat.categoryId === categoryId) || null;
    };

    /**
     * Get localized title for a category
     * @param {string} categoryId - The category ID
     * @returns {string} The localized title or the categoryId if not found
     */
    const getCategoryTitle = (categoryId) => {
        const category = getCategoryById(categoryId);
        if (!category) return categoryId; // Fallback to ID if category not found

        // Extract multi-lingual title
        const title = getMultiLingualString(category.title, userLcid);
        return title || categoryId;
    };

    /**
     * Get localized description for a category
     * @param {string} categoryId - The category ID
     * @returns {string} The localized description or empty string
     */
    const getCategoryDescription = (categoryId) => {
        const category = getCategoryById(categoryId);
        if (!category) return '';

        // Extract multi-lingual description
        return getMultiLingualString(category.description, userLcid);
    };

    const value = {
        categories,
        isLoading,
        error,
        getCategoryById,
        getCategoryTitle,
        getCategoryDescription
    };

    return (
        <CategoryContext.Provider value={value}>
            {children}
        </CategoryContext.Provider>
    );
};

/**
 * Hook to use category context
 * @returns {object} Category context value
 */
export const useCategories = () => {
    const context = useContext(CategoryContext);
    if (!context) {
        throw new Error('useCategories must be used within a CategoryProvider');
    }
    return context;
};

export default CategoryContext;
